// @flow

import React from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import parseDate from 'date-fns/parse'

import type { Action, Poll, PollId } from '../../types'

import { hoverable } from '../utils'

import IfAdmin from '../IfAdmin'
import IfDmpAccess from '../IfDmpAccess'
import { iconForType } from '../Icons'
import { pollPreviewUrl } from '../../urls'
import CopyPollUrl from '../polls/CopyPollUrl'
import HoverTooltip from '../Controls/HoverTooltip'
import CopyEmbedScript from '../polls/CopyEmbedScript'
import CopyWithTooltip from '../polls/CopyWithTooltip'
import CreateQuickyAction from '../polls/CreateQuickyAction'

import { hasDmpPermissions, isAdmin } from '../../stores/usersStore'
import ColorButton from '../Controls/ColorButton'
import { goToPollWhereId } from '../../stores/routing'
import { selectCurrentUser } from '../../stores/currentUser'
import { setPollFeaturedFlag } from '../../stores/pollsStore'
import { fetchPollIntoStoreWhereId } from '../../sagas/fetchPoll'
import { toogleAutomationState } from '../../stores/polls/automated'

type Props = {
  poll: Poll,
  hovered: boolean,
  hasDmpPermissions: boolean,
  open: () => void,
  elementHeight: number,
  isAdmin: boolean,
  isAutomationTab: boolean,
  goToPollWhereId: (id: PollId) => Action,
  setPollFeaturedFlag: (pollId: PollId, featured: boolean) => Action,
  toogleAutomationState: (id: PollId) => Action,
  fetchPollIntoStoreWhereId: (id: PollId) => Action,
}

export function PollItem({
  poll,
  hovered,
  isAdmin,
  hasDmpPermissions,
  elementHeight,
  goToPollWhereId,
  isAutomationTab,
  setPollFeaturedFlag,
  toogleAutomationState,
  fetchPollIntoStoreWhereId,
}: Props) {
  const Icon = iconForType(poll.kind, poll.dmpIntegration)
  const isEditable = isAdmin || !poll.adminOnlyEdit
  const renderPollName = () => {
    const onPollClick = () => {
      fetchPollIntoStoreWhereId(poll.id)
      goToPollWhereId(poll.id)
    }
    if (
      isEditable &&
      (!poll.dmpIntegration || (hasDmpPermissions && poll.dmpIntegration))
    ) {
      return (
        <HoverTooltip
          className="di pointer hover-op-con-dark underline-hover"
          colorClass="white"
          arrowClass="dark-gray"
          backgroundClass="bg-dark-gray"
          tip="Edit"
        >
          <span onClick={onPollClick}>{poll.header || poll.name}</span>
        </HoverTooltip>
      )
    }
    return <span>{poll.header || poll.name}</span>
  }

  const createdAt = parseDate(poll.created)
  const updatedAt = parseDate(poll.modified)

  return (
    <div
      className="dt f6 collapse w-100 gray"
      style={{ height: elementHeight }}
    >
      <div
        className={`dt-row w-100 bg-white bt b--moon-gray ${
          isEditable ? 'hover-bg-near-white' : ''
        }`}
      >
        {!isAutomationTab && (
          <div className="dtc tc w-5 v-mid pa-2">
            <IfAdmin>
              {poll.isPartOfAnExperiment ? (
                <HoverTooltip
                  className="di pointer hover-op-con-dark underline-hover"
                  colorClass="white"
                  arrowClass="dark-gray"
                  backgroundClass="bg-dark-gray"
                  tip="The poll is part of an experiment."
                >
                  <i className="fa fa-flask fa-lg op-brand"></i>
                </HoverTooltip>
              ) : (
                hovered && <i className="fa fa-flask fa-lg"></i>
              )}
            </IfAdmin>
          </div>
        )}

        {!isAutomationTab && (
          <div className="dtc tc w-5 v-mid pa-2">
            <IfAdmin>
              {poll.featured ? (
                <i
                  className="fa fa-star fa-lg op-brand pointer"
                  onClick={() => setPollFeaturedFlag(poll.id, !poll.featured)}
                ></i>
              ) : (
                hovered && (
                  <i
                    className="fa fa-star fa-lg pointer"
                    onClick={() => setPollFeaturedFlag(poll.id, !poll.featured)}
                  ></i>
                )
              )}
            </IfAdmin>
          </div>
        )}
        <div className="dtc w-5 v-mid pa2">
          <Icon size={30} />
        </div>
        <div className="dtc tl w-40 word-wrap v-mid pa2">
          {renderPollName()}
        </div>
        <div className="dtc w-10 v-mid pa2">{poll.customer}</div>
        <div
          className="dtc w-10 v-mid pa2"
          title={
            'created ' +
            createdAt.toLocaleString() +
            ', updated ' +
            updatedAt.toLocaleString()
          }
        >
          {updatedAt.getDate() +
            '.' +
            (updatedAt.getMonth() + 1) +
            '.' +
            updatedAt.getFullYear()}
        </div>
        <div className="dtc w-15 v-mid pv2">
          {poll.sponsored && (
            <span className="bg-op-con-light op-con-dark dib pa1 mr2 mb1">
              sponsored
            </span>
          )}

          {!isAutomationTab && poll.automationConfigured && (
            <span className="bg-op-con-light op-con-dark dib pa1">
              automated
            </span>
          )}

          {isAutomationTab ? (
            poll.automationDisabled ? (
              <span> Removed from Automation </span>
            ) : (
              <IfDmpAccess poll={poll}>
                <ColorButton
                  onClick={() => {
                    if (
                      window.confirm(
                        'Polls that are removed from automation, will no longer be placed in any articles !'
                      )
                    )
                      toogleAutomationState(poll.id)
                  }}
                  className="ml-auto"
                  colorClass="gray hover-white"
                  backgroundClass="bg-near-white hover-bg-op-brand"
                >
                  Remove from Automation
                </ColorButton>
              </IfDmpAccess>
            )
          ) : null}
        </div>
        <div className="dtc w-20 v-mid pa2 pointer tc">
          <CopyWithTooltip
            text={poll.name}
            labelBefore={`Id: ${poll.name} ${
              isAdmin ? 'Customer: ' + poll.customer : ''
            }`}
            labelAfter={'Poll Id copied to clipboard'}
          >
            <i className="fa fa-info-circle fa-lg pr2 pointer hover-op-brand"></i>
          </CopyWithTooltip>

          <HoverTooltip
            className="di"
            colorClass="white"
            arrowClass="dark-gray"
            backgroundClass="bg-dark-gray"
            tip="Preview"
          >
            <i
              className="fa fa-eye fa-lg ph2 pointer gray hover-op-brand link"
              onClick={() => window.open(pollPreviewUrl(poll))}
            ></i>
          </HoverTooltip>

          <CopyPollUrl poll={poll}>
            <i className="poll-url-copy fa fa-link fa-lg ph2 pointer hover-op-brand"></i>
          </CopyPollUrl>

          <CopyEmbedScript poll={poll}>
            <i className="poll-embed-copy fa fa-code fa-lg pl2 pointer hover-op-brand"></i>
          </CopyEmbedScript>

          <IfAdmin>
            <CreateQuickyAction poll={poll}>
              <i className="fa fa-magnet fa-lg pl2 pointer hover-op-brand"></i>
            </CreateQuickyAction>
          </IfAdmin>
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = state => {
  const user = selectCurrentUser(state)
  return {
    isAdmin: user != null && isAdmin(user),
    hasDmpPermissions: user != null && hasDmpPermissions(user),
  }
}

const mapDispatchToProps = {
  goToPollWhereId,
  setPollFeaturedFlag,
  toogleAutomationState,
  fetchPollIntoStoreWhereId,
}

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  hoverable
)(PollItem)



// WEBPACK FOOTER //
// ./src/components/LibraryPage/PollItem.js